/**
 * Admin — Mentorship
 *
 * Master/detail over the mentor candidate set. Candidates are members whose org
 * scores BSS >= 70, PLUS every existing mentor (so a mentor whose score later fell
 * below the bar stays visible and can still be un-assigned).
 *
 * Mentor is an EXPLICIT flag, never derived from the score. "Approved by FRBD Team"
 * is a separate, independent indicator — it gates nothing and may be set on a member
 * who is not a mentor.
 *
 * No auth gate here: `routes/admin.tsx` already gates the whole subtree on
 * super_admin, and the three RPCs re-check server-side.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, BadgeCheck, GraduationCap, Loader2, RefreshCw, Search } from 'lucide-react';
import {
  fetchMentorCandidates,
  setMentorFlag,
  setMentorFrbdApproval,
  type MentorCandidate,
} from '@/api/admin-mentorship';
import { useLatestBSSCalculation } from '@/hooks/use-bss-calculator';
import { BssScoreCard } from '@/components/results/BssScoreCard';
import { PillarRadarChart } from '@/components/results/PillarRadarChart';
import Avatar from '@/components/community/Avatar';

const MENTOR_ELIGIBILITY_SCORE = 70;

function MentorshipPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const candidatesQuery = useQuery({
    queryKey: ['mentor-candidates', debouncedSearch],
    queryFn: () => fetchMentorCandidates(debouncedSearch),
  });

  const candidates = useMemo(() => candidatesQuery.data ?? [], [candidatesQuery.data]);
  // Re-read the selection from the freshest list so the detail panel's buttons
  // reflect a just-completed toggle without holding a stale copy in state.
  const selected = useMemo(
    () => candidates.find((c) => c.organization_id === selectedOrgId) ?? null,
    [candidates, selectedOrgId],
  );

  const mentorMutation = useMutation({
    mutationFn: () => setMentorFlag(selected!.organization_id, !selected!.is_mentor),
    onSuccess: () => candidatesQuery.refetch(),
  });
  const approvalMutation = useMutation({
    mutationFn: () => setMentorFrbdApproval(selected!.organization_id, !selected!.mentor_frbd_approved),
    onSuccess: () => candidatesQuery.refetch(),
  });

  const mentorCount = candidates.filter((c) => c.is_mentor).length;

  return (
    <div className="flex h-full bg-background">
      {/* ---------------------------------------------------------------- list */}
      <div className="w-[420px] flex-shrink-0 border-r border-border flex flex-col">
        <div className="p-4 border-b border-border space-y-3">
          <div>
            <h1 className="text-sm font-semibold text-foreground">Mentorship</h1>
            <p className="text-xs text-muted-foreground">
              Members scoring {MENTOR_ELIGIBILITY_SCORE}+ are eligible. Assignment is manual.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, company or email"
                className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground outline-none focus:border-primary"
              />
            </div>
            <button
              type="button"
              onClick={() => candidatesQuery.refetch()}
              title="Refresh"
              className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <RefreshCw className={`h-4 w-4 ${candidatesQuery.isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-border">
          {candidatesQuery.isLoading && (
            <div className="flex items-center justify-center p-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}
          {candidatesQuery.error && (
            <p className="p-4 text-sm text-destructive">
              {(candidatesQuery.error as Error).message}
            </p>
          )}
          {!candidatesQuery.isLoading && candidates.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              No members are eligible yet.
            </p>
          )}
          {candidates.map((c) => (
            <CandidateRow
              key={c.organization_id}
              candidate={c}
              active={c.organization_id === selectedOrgId}
              onSelect={() => setSelectedOrgId(c.organization_id)}
            />
          ))}
        </div>

        <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          {candidates.length} candidate{candidates.length === 1 ? '' : 's'} · {mentorCount} mentor
          {mentorCount === 1 ? '' : 's'}
        </div>
      </div>

      {/* -------------------------------------------------------------- detail */}
      <div className="flex-1 overflow-y-auto">
        {!selected ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Select a member to view their score breakdown.
          </div>
        ) : (
          <MentorDetail
            candidate={selected}
            onToggleMentor={() => mentorMutation.mutate()}
            onToggleApproval={() => approvalMutation.mutate()}
            mentorPending={mentorMutation.isPending}
            approvalPending={approvalMutation.isPending}
            error={
              (mentorMutation.error as Error | null)?.message ??
              (approvalMutation.error as Error | null)?.message ??
              null
            }
          />
        )}
      </div>
    </div>
  );
}

function CandidateRow({
  candidate,
  active,
  onSelect,
}: {
  candidate: MentorCandidate;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3 p-3 text-left hover:bg-muted/60 ${active ? 'bg-muted' : ''}`}
    >
      <Avatar src={candidate.avatar_url} name={candidate.full_name ?? '?'} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">
          {candidate.company_name ?? 'Unknown company'}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {candidate.full_name}
          {candidate.email ? ` · ${candidate.email}` : ''}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {candidate.is_mentor && (
            <span className="rounded-full border border-info/30 bg-info/10 px-1.5 py-0.5 text-[10px] font-medium text-info">
              Mentor
            </span>
          )}
          {candidate.mentor_frbd_approved && (
            <span className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success">
              <BadgeCheck className="h-3 w-3" /> FRBD
            </span>
          )}
          {candidate.below_threshold && (
            <span className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning">
              <AlertTriangle className="h-3 w-3" /> Below {MENTOR_ELIGIBILITY_SCORE}
            </span>
          )}
        </div>
      </div>
      {/* The denormalised 0 to 100 diagnostic score, labelled so it is never read as the
          Prime Score the member sees (ticket 86d4baew6). */}
      <span className="flex-shrink-0 text-right" title="Diagnostic score, 0 to 100">
        <span className="block text-sm font-semibold tabular-nums text-foreground">
          {candidate.current_bss_score != null ? Math.round(candidate.current_bss_score) : '—'}
        </span>
        <span className="block text-[10px] leading-tight text-muted-foreground">Diagnostic score</span>
      </span>
    </button>
  );
}

function MentorDetail({
  candidate,
  onToggleMentor,
  onToggleApproval,
  mentorPending,
  approvalPending,
  error,
}: {
  candidate: MentorCandidate;
  onToggleMentor: () => void;
  onToggleApproval: () => void;
  mentorPending: boolean;
  approvalPending: boolean;
  error: string | null;
}) {
  // Authoritative latest calculation. RLS lets a super_admin read any org's row;
  // the list's `current_bss_score` is a denormalized copy that can lag behind this.
  const { data: calculation, isLoading } = useLatestBSSCalculation(candidate.organization_id);

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <header className="flex items-start gap-4">
        <Avatar src={candidate.avatar_url} name={candidate.full_name ?? '?'} size="lg" />
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-foreground">{candidate.company_name}</h2>
          <p className="text-sm text-muted-foreground">
            {candidate.full_name}
            {candidate.email ? ` · ${candidate.email}` : ''}
          </p>
        </div>
      </header>

      {candidate.below_threshold && (
        <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>
            This mentor's score is now below {MENTOR_ELIGIBILITY_SCORE}. They remain a mentor —
            removing the flag is a deliberate decision.
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center p-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : !calculation ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <p className="text-sm font-medium text-foreground">No assessment yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            This organisation has never completed the diagnostic, so there is no score breakdown.
          </p>
        </div>
      ) : (
        <>
          <BssScoreCard
            score={calculation.adjustedBssScore}
            trendModifier={calculation.trendModifier}
            lifecycleStage={calculation.lifecycleStage}
            animate={false}
          />
          <PillarRadarChart pillarScores={calculation.pillarScores} />
        </>
      )}

      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Mentor</p>
            <p className="text-xs text-muted-foreground">
              Shown in the community directory with a Mentor badge.
            </p>
          </div>
          <button
            type="button"
            onClick={onToggleMentor}
            disabled={mentorPending}
            className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${
              candidate.is_mentor
                ? 'border border-border text-muted-foreground hover:bg-secondary'
                : 'bg-primary text-primary-foreground hover:opacity-90'
            }`}
          >
            {mentorPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <GraduationCap className="h-4 w-4" />
            )}
            {candidate.is_mentor ? 'Remove mentor' : 'Assign mentor'}
          </button>
        </div>

        <div className="flex items-center justify-between gap-4 border-t border-border pt-3">
          <div>
            <p className="text-sm font-semibold text-foreground">Approved by FRBD Team</p>
            <p className="text-xs text-muted-foreground">
              Admin-only indicator. Not a gate — a member can be a mentor without it.
            </p>
          </div>
          <button
            type="button"
            onClick={onToggleApproval}
            disabled={approvalPending}
            className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${
              candidate.mentor_frbd_approved
                ? 'border border-border text-muted-foreground hover:bg-secondary'
                : 'border border-success/40 bg-success/10 text-success hover:bg-success/20'
            }`}
          >
            {approvalPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <BadgeCheck className="h-4 w-4" />
            )}
            {candidate.mentor_frbd_approved ? 'Remove approval' : 'Mark approved'}
          </button>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>

      <p className="text-xs text-muted-foreground">
        The score in the list is a cached copy written by the diagnostic pipeline and may lag. The
        card and radar above are the latest stored calculation.
      </p>
    </div>
  );
}

export const Route = createFileRoute('/admin/mentorship')({
  component: MentorshipPage,
});
